import { IUserTeamSettingsEntity } from "./IUserTeamSettingsEntity";
import ModelUtils from "../ModelUtils";
import ShiftrObjectModelBase from "../ShiftrObjectModelBase";

const userTeamSettingsIdPrefix = "MYTEAMSET_";

/**
 * User's UserTeamSettings for a team (muted schedule groups for notification control)
 *
 * Stores only muted groups - empty array means all groups are unmuted.
 * Uses eTag for optimistic concurrency control.
 * Pattern matches UserSettingsEntity.
 *
 * Uses "mutedScheduleGroupIds" matching the backend API field name.
 */
export default class UserTeamSettingsEntity extends ShiftrObjectModelBase implements IUserTeamSettingsEntity {
    teamId: string;
    mutedScheduleGroupIds: string[];

    constructor(
        id: string,
        eTag: string,
        teamId: string,
        mutedScheduleGroupIds: string[]
    ) {
        const modelId = id || UserTeamSettingsEntity.generateNewUserTeamSettingId();
        super(modelId, eTag);

        this.teamId = teamId;
        this.mutedScheduleGroupIds = mutedScheduleGroupIds ? [...mutedScheduleGroupIds] : [];
    }

    /**
     * Generate new UserTeamSettings ID (MYTEAMSET_<uuid>)
     * Note: Prefix remains "MYTEAMSET_" for backend compatibility
     */
    public static generateNewUserTeamSettingId(): string {
        return ModelUtils.generateUUIDWithPrefix(userTeamSettingsIdPrefix);
    }

    /**
     * Creates entity from JSON. Returns null if data is null (handles 404 case).
     */
    public static fromJson(jsonData: any): IUserTeamSettingsEntity | null {
        if (!jsonData) {
            return null;
        }

        return new UserTeamSettingsEntity(
            jsonData.id,
            jsonData.eTag,
            jsonData.teamId,
            jsonData.mutedScheduleGroupIds
        );
    }

    /**
     * Serializes entity to JSON. Preserves eTag for optimistic concurrency control.
     */
    static toJson(userTeamSettings: IUserTeamSettingsEntity): IUserTeamSettingsEntity {
        return {
            id: userTeamSettings.id,
            eTag: userTeamSettings.eTag,
            teamId: userTeamSettings.teamId,
            mutedScheduleGroupIds: userTeamSettings.mutedScheduleGroupIds
        };
    }

    /**
     * Clones entity with deep copy of array. Preserves eTag for optimistic updates.
     */
    public static clone(userTeamSettings: IUserTeamSettingsEntity): IUserTeamSettingsEntity | null {
        if (!userTeamSettings) {
            return null;
        }

        return new UserTeamSettingsEntity(
            userTeamSettings.id,
            userTeamSettings.eTag,
            userTeamSettings.teamId,
            userTeamSettings.mutedScheduleGroupIds ? [...userTeamSettings.mutedScheduleGroupIds] : []
        );
    }
}
